import { useMemo } from 'react';

import { Columns2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  ReorderableColumn,
  ReorderableColumnSelector,
} from '@/components/data-table/reorderable-column-selector';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/modules/common';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { useCustomAttributes } from '@/modules/team/hooks/use-custom-attributes';

import {
  parseCustomAttributeColumnKey,
  reconcileUserInfoOrder,
  UserInfoColumnKey,
} from '../../constants';
import usePlannerStore, { UserInfoVisibilityProperty } from '../../store';

/** Hard cap on simultaneously visible user info columns — more doesn't fit the sidebar. */
const MAX_SELECTED_USER_INFO_PROPERTIES = 8;

export function UserInfoVisibilityPopup() {
  const { t } = useTranslation(['authenticated.planner']);
  const {
    userInfoVisibility,
    toggleUserInfoProperty,
    toggleCustomAttributeVisibility,
    userInfoOrder,
    setUserInfoOrder,
  } = usePlannerStore();

  // Workspace custom attributes feed dynamic per-attribute entries (OPS-1466).
  // Same permission gate as the planner filters — the endpoint 403s otherwise.
  const { hasPermission } = usePermissions();
  const canViewAllAssignments = hasPermission(OpsiumPolicy.AssignmentsViewAll);
  const { data: customAttributes } = useCustomAttributes({ enabled: canViewAllAssignments });

  // Fixed reorderable columns' labels (custom attributes are resolved by name below).
  // "billableCapacity" is intentionally absent — it renders as a pinned bottom row.
  const fixedColumnLabels: Record<UserInfoVisibilityProperty, string> = useMemo(
    () => ({
      isManager: t('userInfoVisibility.isManager'),
      memberManager: t('userInfoVisibility.memberManager'),
      department: t('userInfoVisibility.department'),
      role: t('userInfoVisibility.role'),
      seniority: t('userInfoVisibility.seniority'),
      location: t('userInfoVisibility.location'),
      capacityPerDay: t('userInfoVisibility.capacityPerDay'),
      billableCapacity: t('userInfoVisibility.billableCapacity'),
      membershipType: t('userInfoVisibility.membershipType'),
    }),
    [t]
  );

  const customAttributeName = useMemo(
    () => new Map((customAttributes ?? []).map(attribute => [attribute.id, attribute.name])),
    [customAttributes]
  );

  // Reconcile the persisted order with the live column set (new custom attributes
  // appended, deleted ones dropped) so the picker always reflects what exists.
  const orderedKeys = useMemo(
    () =>
      reconcileUserInfoOrder(
        userInfoOrder,
        (customAttributes ?? []).map(attribute => attribute.id)
      ),
    [userInfoOrder, customAttributes]
  );

  const isColumnChecked = (key: UserInfoColumnKey): boolean => {
    const attributeId = parseCustomAttributeColumnKey(key);
    if (attributeId !== null) {
      return !!userInfoVisibility.customAttributes[String(attributeId)];
    }
    return userInfoVisibility[key as UserInfoVisibilityProperty];
  };

  const toggleColumn = (key: UserInfoColumnKey) => {
    const attributeId = parseCustomAttributeColumnKey(key);
    if (attributeId !== null) {
      toggleCustomAttributeVisibility(attributeId);
      return;
    }
    toggleUserInfoProperty(key as UserInfoVisibilityProperty);
  };

  const columnLabel = (key: UserInfoColumnKey): string => {
    const attributeId = parseCustomAttributeColumnKey(key);
    if (attributeId !== null) {
      return customAttributeName.get(attributeId) ?? '';
    }
    return fixedColumnLabels[key as UserInfoVisibilityProperty];
  };

  const selectedCount =
    orderedKeys.filter(isColumnChecked).length + (userInfoVisibility.billableCapacity ? 1 : 0);
  const isSelectionLimitReached = selectedCount >= MAX_SELECTED_USER_INFO_PROPERTIES;

  const columns: ReorderableColumn[] = orderedKeys.map(key => {
    const checked = isColumnChecked(key);
    return {
      key,
      label: columnLabel(key),
      checked,
      disabled: isSelectionLimitReached && !checked,
    };
  });

  // Billable capacity renders as a summary bar in a fixed position, so it's pinned
  // (checkable but not reorderable) — see PlannerTeamMemberInfo.
  const pinnedColumns: ReorderableColumn[] = [
    {
      key: 'billableCapacity',
      label: fixedColumnLabels.billableCapacity,
      checked: userInfoVisibility.billableCapacity,
      disabled: isSelectionLimitReached && !userInfoVisibility.billableCapacity,
    },
  ];

  return (
    <ReorderableColumnSelector
      trigger={
        <Button variant="ghost" className="h-7 w-7 p-0" aria-label={t('userInfoVisibility.title')}>
          <Columns2Icon />
        </Button>
      }
      columns={columns}
      onToggle={toggleColumn}
      onReorder={setUserInfoOrder}
      pinnedColumns={pinnedColumns}
      pinnedDividerLabel={t('userInfoVisibility.fixedPositionDivider')}
      maxSelectedHint={
        isSelectionLimitReached
          ? t('userInfoVisibility.maxSelectedHint', { max: MAX_SELECTED_USER_INFO_PROPERTIES })
          : undefined
      }
      reorderHandleLabel={t('userInfoVisibility.reorderHandle')}
    />
  );
}
